import React, { useEffect, useRef, useState } from 'react';
import tw from 'twin.macro';
import { css } from '@emotion/react';
import { animate, motion, useDragControls, useMotionValue } from 'framer-motion';

import config from '$/config';

interface LogMessage {
  type: 'log' | 'debug' | 'error';
  message: string;
}

const DevConsoleWindow: React.FC = () => {
  const [messages, setMessages] = useState<LogMessage[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = (): void => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (!config.isLocal) return;

    const originalLog = console.log;
    const originalDebug = console.debug;
    const originalError = console.error;

    console.log = (...args: unknown[]): void => {
      const message = args.map((arg) => JSON.stringify(arg)).join(' ');
      originalLog(...args);
      setMessages((prevMessages) => [...prevMessages, { type: 'log', message }]);
    };

    console.debug = (...args: unknown[]): void => {
      const message = args.map((arg) => JSON.stringify(arg)).join(' ');
      originalDebug(...args);
      setMessages((prevMessages) => [...prevMessages, { type: 'debug', message }]);
    };

    console.error = (...args: unknown[]): void => {
      const message = args.map((arg) => JSON.stringify(arg)).join(' ');
      originalError(...args);
      setMessages((prevMessages) => [...prevMessages, { type: 'error', message }]);
    };

    return (): void => {
      console.log = originalLog;
      console.debug = originalDebug;
      console.error = originalError;
    };
  }, []);

  useEffect(scrollToBottom, [messages]);

  const x = useMotionValue(0);
  const y = useMotionValue(7);
  const opacity = useMotionValue(0);
  const refBar = React.useCallback(
    (node: HTMLDivElement | null) => {
      if (!node) return;
      animate(opacity, 1);
    },
    [opacity]
  );

  const dragControls = useDragControls();
  const startDrag = React.useCallback(
    (e) => {
      dragControls?.start(e);
    },
    [dragControls]
  );

  return (
    <motion.div
      className='ConsoleWindow z-player-test-bar'
      css={[
        tw`text-black`,
        css`
          position: fixed;
          bottom: 0;
          left: 0;
          right: 0;
          height: 350px;
          z-index: 999999999999999999;
          width: 100%;
          color: #fff;
          box-sizing: border-box;
          background: #000;
          opacity: 0.9;

          .scroll-area {
            overflow-y: auto;
            white-space: pre-wrap; /* Add this line */
            word-wrap: break-word; /* Add this line */
            font-family: monospace;
            font-size: 9pt !important;
            padding: 10px;
            padding-top: 0;
            width: 100%;
            height: 100%;
          }

          .log-message.log {
            color: #bada55;
          }

          .log-message.debug {
            color: #34a8eb;
          }

          .log-message.error {
            color: #eb4034;
          }
        `,
      ]}
      drag
      dragControls={dragControls}
      dragListener={false}
      dragMomentum={false}
      style={{ x, y, opacity }}
      whileDrag={{ cursor: 'grabbing' }}
    >
      <div
        ref={refBar}
        className='text-gray-400 bg-black text-xl'
        css={css`
          position: sticky;
          top: 0;
          left: 0;
          right: 0;
          height: 25px;
          cursor: grab;
          z-index: 1;
          padding-left: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
        `}
        onTouchStart={startDrag}
        onPointerDown={startDrag}
      >
        <div
          css={css`
            height: 5px;
            border-radius: 2.5px;
            width: 50%;
            background: #fff;
          `}
        />
      </div>
      <div className='scroll-area'>
        {messages.map((msg, index) => (
          <div key={index} className={`log-message ${msg.type}`}>
            {msg.message}
          </div>
        ))}
      </div>
      <div ref={messagesEndRef} />
    </motion.div>
  );
};

const ConsoleWindow: React.FC = () => {
  if (!config.isLocal || !config.showConsoleWindow) return null;

  return <DevConsoleWindow />;
};

export default ConsoleWindow;
